import {AiVideoGenerationErrorCode, getAiVideoGenerationErrorMessage} from '@Libraries/ai-video.library';
import type {VideoData} from '@Libraries/add-media-library';
import {ElementDataType} from '@Libraries/add-media-library';
import type {UserVideoSource} from '@Libraries/user-video-library';
import {isUserLoggedIn} from '@Libraries/user.library';
import {pollableJob, resumePollableJob} from '@Libraries/pollable-job.library';
import {PollError, PollFailError, PollTimeoutError} from '@Libraries/pollable-job.library.types';
import type {PollEnvelope, PollKickoffAck, PollTransportParams} from '@Libraries/pollable-job.library.types';

export const POSTER_ANIMATION_GENERATION_POLL_TIMEOUT_MS = 1_200_000;

/** Mirrors the backend AiAnimationSourceType enum; the value round-trips to ai_poster_animation.source_type. */
export enum AiAnimationSourceType {
  PAGE = 'page',
  ITEM = 'item',
}

export interface GeneratePosterAnimationParams {
  imageData: string;
  quality: string;
  generateAudio: boolean;
  prompt: string;
  posterHashedId: string;
  sourceType: AiAnimationSourceType;
  itemUid: string;
  sourceItemType: string;
}

export interface PosterAnimationData {
  id: number;
  hashedFilename: string;
  width: number;
  height: number;
  duration: number;
  addedToDesign: boolean;
  hasAudio: boolean;
  hasTransparency: boolean;
  sourceType: AiAnimationSourceType;
  itemUid: string | null;
  prompt: string;
  url: string;
  thumbUrl: string;
  thumbVideoUrl: string;
}

interface PosterAnimationGenerationDoneData {
  generatedAnimation: PosterAnimationData;
}

export class PosterAnimationGenerationError extends Error {
  public readonly code?: string;

  constructor(code?: string) {
    super(code);
    this.name = 'PosterAnimationGenerationError';
    this.code = code;
  }
}

const initiatePosterAnimationGeneration = async (params: GeneratePosterAnimationParams): Promise<PollKickoffAck> => {
  return (await window.PMW.writeLocal('aiposteranimation/generateVideo', params)) as PollKickoffAck;
};

const checkPosterAnimationGenerationStatus = async (ack: PollKickoffAck, transportParams: PollTransportParams): Promise<PollEnvelope<PosterAnimationGenerationDoneData>> => {
  return (await window.PMW.readLocal('aiposteranimation/getVideoGenerationStatus', {
    pollingKey: ack.pollingKey,
    ...transportParams,
  })) as PollEnvelope<PosterAnimationGenerationDoneData>;
};

const unwrapPosterAnimationPoll = async (poll: () => Promise<PosterAnimationGenerationDoneData>): Promise<PosterAnimationData> => {
  try {
    const data = await poll();
    return data.generatedAnimation;
  } catch (e) {
    if (e instanceof PollFailError || e instanceof PollError) {
      throw new PosterAnimationGenerationError(e.code);
    }
    if (e instanceof PollTimeoutError) {
      throw new Error('Poster animation generation timed out without producing a result', {cause: e});
    }
    throw e;
  }
};

export const generatePosterAnimation = async (params: GeneratePosterAnimationParams, onAck?: (ack: PollKickoffAck) => void): Promise<PosterAnimationData> => {
  return unwrapPosterAnimationPoll(() => {
    return pollableJob<PosterAnimationGenerationDoneData>({
      kickoff: () => initiatePosterAnimationGeneration(params),
      status: checkPosterAnimationGenerationStatus,
      timeoutMs: POSTER_ANIMATION_GENERATION_POLL_TIMEOUT_MS,
      onAck,
    });
  });
};

/**
 * Watches a generation this browser started but is no longer observing, after a reload closed the
 * original poll. Joining is read-only — it never starts work — so a key whose job has already
 * finished, expired, or belongs to nobody resolves or fails immediately instead of double-charging.
 */
export const resumePosterAnimationGeneration = async (pollingKey: string, timeoutMs: number): Promise<PosterAnimationData> => {
  return unwrapPosterAnimationPoll(() => {
    return resumePollableJob<PosterAnimationGenerationDoneData>({
      pollingKey,
      status: checkPosterAnimationGenerationStatus,
      timeoutMs,
    });
  });
};

export const getGeneratedPosterAnimations = async (sourceType: AiAnimationSourceType, limit = 20, offset = 0): Promise<PosterAnimationData[]> => {
  if (!isUserLoggedIn()) {
    return [];
  }
  return (await window.PMW.readLocal('aiposteranimation/getGeneratedAnimations', {sourceType, limit, offset})) as PosterAnimationData[];
};

interface UploadGeneratedPosterAnimationResponse {
  hashedFilename: string;
  extension: string;
  duration: number;
  frameRate: number;
  source: UserVideoSource;
  hasTransparency: boolean;
  width: number;
  height: number;
}

export const uploadGeneratedPosterAnimation = async (idAnimation: number): Promise<VideoData> => {
  const response = (await window.PMW.writeLocal('aiposteranimation/uploadGeneratedAnimation', {idAnimation})) as UploadGeneratedPosterAnimationResponse;
  return {
    type: ElementDataType.VIDEO,
    hashedFilename: response.hashedFilename,
    extension: response.extension,
    duration: response.duration,
    frameRate: response.frameRate,
    source: response.source,
    hasTransparency: response.hasTransparency,
    width: response.width,
    height: response.height,
  };
};

// Mirrors the backend AnimationGenerationErrorCode buckets carried by VeoSafetyFilterException.
enum PosterAnimationSafetyErrorCode {
  SAFETY_CHILDREN = 'safety_children',
  SAFETY_SENSITIVE = 'safety_sensitive',
  SAFETY_COPYRIGHT = 'safety_copyright',
}

export const getPosterAnimationErrorMessage = (code: string | undefined): string => {
  switch (code) {
    case AiVideoGenerationErrorCode.INSUFFICIENT_CREDITS:
      return window.i18next.t('pmwjs_purchase_more_credits_for_ai');
    case PosterAnimationSafetyErrorCode.SAFETY_CHILDREN:
      return window.i18next.t('pmwjs_animation_creation_error_children');
    case PosterAnimationSafetyErrorCode.SAFETY_SENSITIVE:
      return window.i18next.t('pmwjs_animation_creation_error_sensitive');
    case PosterAnimationSafetyErrorCode.SAFETY_COPYRIGHT:
      return window.i18next.t('pmwjs_animation_creation_error_copyright');
    default:
      return getAiVideoGenerationErrorMessage(code);
  }
};
